<div class="my-8 max-w-7xl mx-auto px-4">

    <!-- Header Section with Back Button -->
    <div class="w-full flex flex-col gap-4 mb-6">
        <app-btn-back routeTo=".." icon="fa fa-close" btnText="Cancel"></app-btn-back>
        <h1 class="font-semibold text-2xl capitalize text-primary-500 mb-0">New Member</h1>
    </div>

    <!-- Warnings / Privacy Notices -->
    <div class="flex flex-col gap-4 w-full mb-6">
        <!-- Data Privacy Alert -->
        <div class="flex flex-col md:flex-row items-start md:items-center bg-red-50/50 border border-red-200/60 rounded-xl p-4 gap-3">
            <div class="bg-red-100 rounded-full h-10 w-10 flex items-center justify-center shrink-0">
                <i class="fas fa-triangle-exclamation text-red-500 text-lg"></i>
            </div>
            <div class="flex-1 text-xs">
                <span class="text-red-700 font-bold block mb-1">Data Privacy Responsibility</span>
                <p class="text-blueGray-600 leading-relaxed mb-1">
                    Note that member's data privacy is the responsibility of the group admin(s) and member sharing his/her data. AtChurch does not take any responsibility for the user data being shared.
                </p>
                <p class="text-primary-500 leading-relaxed font-medium">
                    We recommend that member create an account on AtChurch platform to have full control over their data.
                </p>
            </div>
        </div>

        <!-- Direct Platform Access Alert -->
        <div class="flex flex-col md:flex-row items-start md:items-center bg-primary-50/30 border border-primary-200/50 rounded-xl p-4 gap-3">
            <div class="bg-primary-100/50 rounded-full h-10 w-10 flex items-center justify-center shrink-0">
                <i class="fas fa-info text-primary-500 text-base"></i>
            </div>
            <div class="flex-1 text-xs">
                <span class="text-primary-600 font-bold block mb-1">No Direct Platform Access</span>
                <p class="text-blueGray-600 leading-relaxed mb-1">
                    Kindly note that member's created here will not have access to AtChurch platform.
                </p>
                <p class="text-primary-500 leading-relaxed font-medium">
                    This membership cannot be transferred to the member in future.
                </p>
            </div>
        </div>
    </div>

    <!-- Form Section -->
    <div class="w-full">
        <form [formGroup]="pageForm" (ngSubmit)="create()">

            <!-- Section 1: Personal Details -->
            <span class="text-primary-500 text-sm font-semibold mb-2 block uppercase tracking-wider">Personal Details</span>
            <div class="w-full border border-blueGray-200/60 rounded-xl p-5 mb-6 flex flex-col gap-4">
                
                <!-- Member Image -->
                <div class="w-full">
                    <label class="block uppercase text-blueGray-600 text-xs font-bold mb-1">
                        Member Image
                    </label>
                    <span class="text-xs text-primary-300 block mb-2">Image file up to 5 Mb only (jpg, jpeg, png)</span>
                    <div class="rounded overflow-hidden border border-blueGray-200/50 h-[130px]">
                        <uppy-dashboard [uppy]="uppy" [props]="dashboardProps"></uppy-dashboard>
                    </div>
                </div>

                <!-- Name -->
                <div class="w-full">
                    <label class="block uppercase text-blueGray-600 text-xs font-bold mb-1">
                        Name *
                    </label>
                    <input type="text"
                        class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded capitalize text-sm shadow focus:outline-none focus:ring-2 focus:ring-primary-500 w-full ease-linear transition-all duration-150"
                        placeholder="Full Name" formControlName="name" name="name" required>
                    <div
                        class="{{pageForm.get('name')?.invalid && (pageForm.get('name')?.dirty || pageForm.get('name')?.touched) ? 'visible' : 'invisible' }} pt-1 h-5 text-red-500 text-xs">
                        @if (pageForm.get('name')?.errors?.['required']) {
                        <p>
                            Value is required
                        </p>
                        }
                    </div>
                </div>

                <!-- DOB & Gender -->
                <div class="flex flex-col md:flex-row gap-4 w-full">
                    <div class="flex-1">
                        <label class="block uppercase text-blueGray-600 text-xs font-bold mb-1">
                            Date of Birth
                        </label>
                        <input type="date" name="dob" formControlName="dob" placeholder="dd/mm/yyyy"
                            class="border-0 px-3 py-3 placeholder:text-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring-2 focus:ring-primary-500 w-full ease-linear transition-all duration-150"
                            min="1900-01-01" [max]="today">
                    </div>
                    <div class="flex-1">
                        <label class="block uppercase text-blueGray-600 text-xs font-bold mb-1">
                            Gender
                        </label>
                        <app-gender formControlName="gender"></app-gender>
                    </div>
                </div>
            </div>

            <!-- Section 2: Profession & Contact Info -->
            <span class="text-primary-500 text-sm font-semibold mb-2 block uppercase tracking-wider">Profession & Contact</span>
            <div class="w-full border border-blueGray-200/60 rounded-xl p-5 mb-6 flex flex-col gap-4">
                
                <!-- Profession -->
                <div class="w-full">
                    <label class="block uppercase text-blueGray-600 text-xs font-bold mb-1">
                        Profession
                    </label>
                    <input type="text"
                        class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded normal-case text-sm shadow focus:outline-none focus:ring-2 focus:ring-primary-500 w-full ease-linear transition-all duration-150"
                        placeholder="e.g. Engineer, Doctor, Teacher" formControlName="profession" name="profession">
                </div>

                <!-- Contact & Email -->
                <div class="flex flex-col md:flex-row gap-4 w-full">
                    <div class="flex-1">
                        <label class="block uppercase text-blueGray-600 text-xs font-bold mb-1">
                            Contact
                        </label>
                        <app-contact formControlName="contact"></app-contact>
                    </div>
                    <div class="flex-1">
                        <label class="block uppercase text-blueGray-600 text-xs font-bold mb-1">
                            Email
                        </label>
                        <input type="email" formControlName="email" id="email" name="email"
                            class="border-0 placeholder-blueGray-300 text-blueGray-600 bg-white rounded normal-case text-sm shadow focus:outline-none focus:ring-2 focus:ring-primary-500 w-full py-3 px-3 ease-linear transition-all duration-150"
                            placeholder="email@example.com" />
                        <div
                            class="{{ pageForm.get('email')?.invalid && (pageForm.get('email')?.dirty || pageForm.get('email')?.touched) ? 'visible' : 'invisible'}} pt-1 h-5 text-red-500 text-xs">
                            <p>{{pageForm.get('email')?.errors?.['email']}}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Section 3: Address & Location -->
            <span class="text-primary-500 text-sm font-semibold mb-2 block uppercase tracking-wider">Address & Location</span>
            <div class="w-full border border-blueGray-200/60 rounded-xl p-5 mb-6 flex flex-col gap-4">
                
                <!-- Flat & Building -->
                <div class="flex flex-col md:flex-row gap-4 w-full">
                    <div class="flex-1">
                        <label class="block uppercase text-blueGray-600 text-xs font-bold mb-1">
                            Flat / Villa No
                        </label>
                        <input type="text" name="buildingNo" formControlName="buildingNo"
                            placeholder="House/Flat Number"
                            class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring-2 focus:ring-primary-500 w-full ease-linear transition-all duration-150">
                    </div>
                    <div class="flex-1">
                        <label class="block uppercase text-blueGray-600 text-xs font-bold mb-1">
                            Building Name
                        </label>
                        <input type="text" name="buildingName" formControlName="buildingName"
                            placeholder="Building Name"
                            class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded normal-case text-sm shadow focus:outline-none focus:ring-2 focus:ring-primary-500 w-full ease-linear transition-all duration-150">
                    </div>
                </div>

                <!-- Address Lines -->
                <div class="flex flex-col md:flex-row gap-4 w-full">
                    <div class="flex-1">
                        <label class="block uppercase text-blueGray-600 text-xs font-bold mb-1">
                            Address Line 1
                        </label>
                        <input type="text" name="addressLine1" formControlName="addressLine1"
                            placeholder="Address Line 1"
                            class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded normal-case text-sm shadow focus:outline-none focus:ring-2 focus:ring-primary-500 w-full ease-linear transition-all duration-150">
                    </div>
                    <div class="flex-1">
                        <label class="block uppercase text-blueGray-600 text-xs font-bold mb-1">
                            Address Line 2
                        </label>
                        <input type="text" name="addressLine2" formControlName="addressLine2"
                            placeholder="Address Line 2"
                            class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded normal-case text-sm shadow focus:outline-none focus:ring-2 focus:ring-primary-500 w-full ease-linear transition-all duration-150">
                    </div>
                </div>

                <!-- City & Country -->
                <div class="flex flex-col md:flex-row gap-4 w-full">
                    <div class="flex-1">
                        <label class="block uppercase text-blueGray-600 text-xs font-bold mb-1">
                            City *
                        </label>
                        <input type="text" name="city" formControlName="city" placeholder="City" required
                            class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded normal-case text-sm shadow focus:outline-none focus:ring-2 focus:ring-primary-500 w-full ease-linear transition-all duration-150">
                        <div
                            class="{{pageForm.get('city')?.invalid && (pageForm.get('city')?.dirty || pageForm.get('city')?.touched) ? 'visible' : 'invisible' }} pt-1 h-5 text-red-500 text-xs">
                            @if (pageForm.get('city')?.errors?.['required']) {
                            <p>
                                Value is required
                            </p>
                            }
                        </div>
                    </div>
                    <div class="flex-1">
                        <label class="block uppercase text-blueGray-600 text-xs font-bold mb-1">
                            Country *
                        </label>
                        <app-country formControlName="country"></app-country>
                    </div>
                </div>

                <!-- Map Location -->
                <div class="w-full border border-blueGray-200/60 rounded-xl overflow-hidden p-4 h-128 shadow-inner">
                    <app-add-location [location]="pageForm.get('position')?.value"
                        (locationChange)="setLocation($event)" label="Location"
                        description="Location is only visible to group members"></app-add-location>
                </div>
            </div>

            <!-- Submit Button & Feedback -->
            <div class="w-full mt-4 flex flex-col items-start gap-2">
                <app-btn-submit [isDisabled]="!pageForm.valid || working" [isLoading]="working"
                    [actionText]="'Create'" [icon]="'fa fa-save'"></app-btn-submit>
                @if (error) {
                <p class="text-red-500 text-xs font-medium">{{error}}</p>
                }
            </div>
        </form>
    </div>
</div>